/* screens/workspace/canvas/TTKCanvas.jsx — inline-канвас ТТК (S6).
   Read-only превью карты по ГОСТ внутри ленты: мета+тип, масштаб порций, состав
   с RecipeBindingBadge (read-only), технология, показатели качества, КБЖУ, food cost,
   аллергены, хранение. Вход в редактор — «Открыть в редакторе →».
   Переиспользует S3-блоки: IngredientsTable, KbjuTable, CostSummaryBlock, PortionsSelector.
   Источник: 07 §2.9, 05 сценарий 8. ХЕНДОФФ → frontend/components/chat/TTKCanvas.jsx. */

import { useChat } from '../../../app/providers/ChatProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { IngredientsTable } from '../../../components/kitchen/IngredientsTable.jsx';
import { CostSummaryBlock, KbjuTable } from '../../../components/ttk/TTKViewBlocks.jsx';
import { KindBadge, Pill } from '../../../components/ui/Badge.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';
import { CanvasErrorState, CanvasMetrics, CanvasSection, CanvasSkeleton, CanvasViewForm, useCanvasLoad } from './CanvasViewForm.jsx';

  const { useNavigate } = RR;

  const KIND_LABEL = { dish: 'Блюдо', semiproduct: 'Полуфабрикат' };

  function QualityGrid({ d }) {
    const rows = [
      ['Внешний вид', d.appearance], ['Консистенция', d.texture],
      ['Вкус', d.taste], ['Запах', d.smell],
      ['Микробиология', d.microbiology],
    ].filter(([, v]) => v);
    if (!rows.length) return null;
    return (
      <dl className="cv-quality">
        {rows.map(([k, v]) => (<div className="cv-quality__row" key={k}><dt>{k}</dt><dd>{v}</dd></div>))}
      </dl>
    );
  }

  function TTKCanvas({ spec, onClose, panel }) {
    const { kind, data, partial, error, retry } = useCanvasLoad(spec);
    const navigate = useNavigate();
    const toast = useToast();
    const chat = useChat ? useChat() : null;

    const id = data ? data.id : spec.id;
    const editHref = `/chat`; // канвас живёт в чате; «Открыть в редакторе» уводит в Кухню:
    const kitchenHref = `/kitchen?tab=${data && data.kind === 'semiproduct' ? 'semiproducts' : 'recipes'}&edit=ttk-${id}`;
    const historyHref = kitchenHref + '&etab=history';   // «История →» открывает вкладку версий (S13 §2.2)

    const header = {
      eyebrow: 'Конструктор ТТК', icon: 'book',
      title: (data && data.title) || (partial && partial.title) || 'Карта ТТК',
      historyHref, onClose, panel,
    };

    if (kind === 'loading') {
      return <CanvasViewForm {...header} title={header.title}><CanvasSkeleton note="Собираю технологическую карту…" /></CanvasViewForm>;
    }
    if (kind === 'error') {
      return <CanvasViewForm {...header}><CanvasErrorState error={error} message="Не удалось загрузить ТТК" notFoundMessage="ТТК не найдена или была удалена." onRetry={retry} /></CanvasViewForm>;
    }
    if (kind === 'partial') {
      return (
        <CanvasViewForm {...header}>
          <div className="cv-partial">
            <div className="cv-partial__badge"><Icon name="spinner" size={13} /> Собираю состав…</div>
            <ul className="cv-partial__list">
              {(partial.ingredients_preview || []).map((n, i) => (
                <li key={i} className="cv-partial__item"><span className="cv-partial__dot" />{n}</li>
              ))}
              <li className="cv-partial__item cv-partial__item--ghost"><span className="cv-partial__dot" /><span className="cv-skel-inline" /></li>
            </ul>
          </div>
        </CanvasViewForm>
      );
    }

    // ready
    const d = data;
    const allergens = [...new Set([...(d.allergens || []), ...(d.allergens_auto || [])])];
    const spawnableRows = (d.ingredient_rows || []).filter((r) => r.target_kind === 'ttk' && (r.target_id == null));
    const spawnPf = (row) => {
      if (!chat || !chat.spawnChat) { toast.show('Чат недоступен', 'muted'); return; }
      chat.spawnChat({
        skill: 'ttk-generate', spawnFor: id, parentTitle: d.title,
        title: 'П/ф «' + (row.name_raw || 'полуфабрикат') + '»',
        prefill: 'Разработай полуфабрикат «' + (row.name_raw || '') + '» для ТТК «' + d.title + '».',
      });
    };
    const footer = (
      <div className="cv-foot-actions">
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => toast.show('Скачивание .docx недоступно в прототипе', 'muted')}>
          <Icon name="download" size={14} /><span className="btn__label">Скачать .docx</span>
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(kitchenHref)}>
          <span className="btn__label">Открыть в редакторе</span><Icon name="arrow-up-right" size={14} />
        </button>
      </div>
    );

    return (
      <CanvasViewForm {...header} openHref={kitchenHref} openLabel="Открыть" footer={footer}>
        <div className="cv-meta-row">
          <KindBadge kind={d.kind === 'semiproduct' ? 'semi' : 'dish'} size="sm" />
          {d.output_g != null && <span className="cv-meta"><span className="cv-meta__k">Выход</span><span className="num">{fmt.grams(d.output_g)}</span></span>}
          {d.selling_price_rub != null && <span className="cv-meta"><span className="cv-meta__k">Цена</span><span className="num">{fmt.money(d.selling_price_rub)}</span></span>}
        </div>

        <CanvasSection title="Состав">
          {d.ingredient_rows && d.ingredient_rows.length
            ? <IngredientsTable rows={d.ingredient_rows} readOnly showBinding
                onRowChange={() => {}} onDelete={() => {}} onOpenTarget={() => navigate(kitchenHref)} />
            : <p className="cv-empty-note text-mute">Состав ещё не заполнен — это заглушка (stub). Откройте в редакторе, чтобы добавить ингредиенты.</p>}
          {spawnableRows.length > 0 && (
            <div className="cv-spawn">
              <div className="cv-spawn__head">
                <Icon name="sparkles" size={15} />
                <span>Полуфабрикаты без своей ТТК — разработайте в отдельном чате:</span>
              </div>
              <ul className="cv-spawn__list">
                {spawnableRows.map((r, i) => (
                  <li key={i} className="cv-spawn__row">
                    <span className="cv-spawn__name truncate">{r.name_raw || 'Полуфабрикат'}</span>
                    <button type="button" className="btn btn--secondary btn--sm cv-spawn__btn" onClick={() => spawnPf(r)}>
                      <Icon name="sparkles" size={14} /><span className="btn__label">Разработать п/ф</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CanvasSection>

        {d.technology && (
          <CanvasSection title="Технология"><p className="cv-text">{d.technology}</p></CanvasSection>
        )}

        <QualitySectionMaybe d={d} />

        {(d.kbju_per_portion || d.kbju_per_100g) && (
          <CanvasSection title="Пищевая ценность">
            <KbjuTable per_portion={d.kbju_per_portion} per_100g={d.kbju_per_100g} />
          </CanvasSection>
        )}

        <CanvasSection title={d.kind === 'semiproduct' ? 'Себестоимость' : 'Себестоимость и food cost'}>
          <CostSummaryBlock
            cost_per_portion_rub={d.cost_per_portion_rub}
            cost_per_100g_rub={d.cost_per_100g_rub} output_g={d.output_g}
            selling_price_rub={d.selling_price_rub} cost_status={d.cost_status} fc_pct={d.fc_pct} kind={d.kind} />
        </CanvasSection>

        {allergens.length > 0 && (
          <CanvasSection title="Аллергены" hint="ТР ТС 022/2011">
            <div className="cv-chips">{allergens.map((a) => <Pill key={a} tone="warn" size="sm" glyph={false}>{a}</Pill>)}</div>
          </CanvasSection>
        )}

        {d.plating_and_service && (
          <CanvasSection title="Оформление и подача"><p className="cv-text">{d.plating_and_service}</p></CanvasSection>
        )}
        {d.storage_conditions && (
          <CanvasSection title="Хранение"><p className="cv-text">{d.storage_conditions}</p></CanvasSection>
        )}
        {/* Типовые ГОСТ-разделы (Р1/Р2) — в конце, чтобы шаблонный текст
            не оттеснял рецептурную часть наверху канваса */}
        {d.application_scope && (
          <CanvasSection title="Область применения"><p className="cv-text">{d.application_scope}</p></CanvasSection>
        )}
        {d.raw_material_requirements && (
          <CanvasSection title="Требования к сырью"><p className="cv-text">{d.raw_material_requirements}</p></CanvasSection>
        )}
      </CanvasViewForm>
    );
  }

  function QualitySectionMaybe({ d }) {
    if (!(d.appearance || d.texture || d.taste || d.smell || d.microbiology)) return null;
    return <CanvasSection title="Показатели качества"><QualityGrid d={d} /></CanvasSection>;
  }

export { TTKCanvas };
